import { AppKey, AppMode, createApp } from '@inno-sdks/files'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { logger } from 'shared/services/logger'
import { lsGetItem } from 'shared/utils/local-storage'

export type FilesApp = ReturnType<typeof createApp>

type InitFilesSdkResult = {
  filesSdk: FilesApp | null
  success: boolean
}

// The SDK initialization involves setup work that shouldn't be repeated on every
// call. The instance bakes the auth token into a static Authorization header at
// construction, so we cache it keyed by the token and rebuild whenever the token
// changes (login, refresh-token flow, logout) — otherwise a refreshed token
// would keep hitting the API with the stale (expired) one.
let cachedSdk: FilesApp | null = null
let cachedToken: string | null = null

export const initFilesSdk = (): InitFilesSdkResult => {
  try {
    const token = lsGetItem(LocalStorageKey.USER)?.access_token ?? null

    if (!cachedSdk || token !== cachedToken) {
      cachedSdk = createApp({
        apiUrl: process.env.REACT_APP_CLUSTER_FILES_SERVICE!,
        token: token ?? '',
        mode: AppMode.Files,
        appKey: AppKey.Desktop
      })
      cachedToken = token
    }

    return { filesSdk: cachedSdk, success: !!token }
  } catch (error) {
    cachedSdk = null
    cachedToken = null
    logger.exception(
      'files.sdk_initialization_failed',
      error,
      {},
      { category: 'business' }
    )
    return { filesSdk: null, success: false }
  }
}

/** Drops the cached SDK so the next `initFilesSdk` rebuilds (e.g. on logout). */
export const resetFilesSdkCache = () => {
  cachedSdk = null
  cachedToken = null
}
